import { apiBlob, apiFetch } from "../api/client";

/**
 * LES FACTURES — le seul document COMPTABLE remis au participant.
 *
 *   GET  /v2/domains/:d/events/:e/invoicesV2/me            api-v1   la liste des réglées
 *   POST /domains/:d/events/:e/users/:u/invoices/:id       apix     le PDF
 *
 * ⚠️ DEUX ROUTES DE TÉLÉCHARGEMENT EXISTENT, ET ON N'A PAS PRIS CELLE D'API-V1. La
 * sienne (`invoicesV2/download`) fabrique le PDF sur un fond d'image nommé par
 * `FO_MEETING_ARTICLES_BILL_ID`, et répond **404 « Bill not found »** quand ce réglage
 * manque — ce qui est le cas de l'événement de test, et sans doute de beaucoup d'autres.
 * Celle d'apix n'a besoin d'AUCUN réglage obligatoire : elle compose le document
 * elle-même et le rend directement. C'est elle que la V1 appelle, et c'est elle ici.
 *
 * ⚠️ DEUX GÉNÉRATEURS, DEUX FORMATS DE FACTURE, ET IL FAUT LES APPARIER. Le drapeau
 * `gayakoa=true` choisit un SECOND générateur, et ce n'est pas « le cassé » contre « le
 * bon » : chacun ne sait lire que les factures d'UN des deux créateurs, parce que les
 * deux n'écrivent pas les mêmes clés dans `bill.resume` —
 *
 *   `invoices/create` (apix)          écrit `totalHTString`, arrondis   → `generatePDF`
 *   `invoicesV2` (api-v1, le PAIEMENT) écrit `totalHT`, valeurs brutes  → `gayakoa=true`
 *
 * Mesuré en base : 38 797 factures du premier format, 205 du second. Servi au mauvais
 * générateur, un document sort avec « undefined € » sur les trois totaux et le montant
 * réglé — vérifié dans les DEUX sens, sur de vraies factures.
 *
 * ⚠️ ET C'EST LE PAIEMENT QUI PRODUIT LE FORMAT MINORITAIRE. Toute facture réglée par
 * Stripe — donc toute facture que la V2 fait naître — porte les clés brutes. Choisir
 * `generatePDF` pour tout le monde, ce que j'avais fait, rendait donc illisibles
 * exactement les factures de la V2. La V1, elle, force `gayakoa=true` partout et casse
 * l'immense majorité des autres. Aucun des deux réglages fixes n'est bon : on regarde ce
 * que la facture porte.
 *
 * ⚠️ LA LANGUE SE PASSE, SANS QUOI LA FACTURE EST EN ANGLAIS. Le générateur retombe sur
 * `EN` par défaut (`generatePDF(invoices, settings, language = 'EN')`) : un client
 * français recevait « Invoice / Unit Price / VAT rate ». Il ne connaît QUE `FR` et `EN` —
 * une facture italienne n'existe pas, et demander `IT` rendrait de l'anglais.
 */

/** Une facture réglée, telle qu'on l'affiche. */
export type Invoice = {
  id: number;
  /** Le numéro tel que le serveur l'a attribué : « 2025/35 ». */
  numero: string;
  amountExclTax: number;
  amountInclTax: number;
  /** La date portée par la facture (« 31/03/2025 »), vide si elle n'en a pas. */
  date: string;
  /**
   * QUEL GÉNÉRATEUR SAIT LIRE CETTE FACTURE — déduit de ses propres clés, jamais
   * supposé. Voir l'en-tête du fichier : les deux créateurs n'écrivent pas les mêmes,
   * et servir la mauvaise rend un document dont tous les totaux valent « undefined € ».
   */
  generator: "standard" | "gayakoa";
};

type RawInvoice = {
  id?: number;
  label?: string | null;
  price?: unknown;
  priceTva?: unknown;
  /* Booléen côté Sequelize, mais un 0/1 brut ne doit pas tromper la lecture. */
  withTva?: boolean | number | null;
  bill?: { date?: unknown; resume?: Record<string, unknown> | null } | null;
};

const count = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) ? v : 0;

/**
 * CE QUE LA ROUTE REND, RAMENÉ À CE QU'ON AFFICHE.
 *
 * ⚠️ SEULES LES PAYÉES REMONTENT, et c'est la route qui le garantit
 * (`where: { isPaid: true }`). Une facture non réglée n'est pas un document comptable :
 * c'est une intention de paiement, et la proposer au téléchargement ferait circuler une
 * pièce qui n'atteste de rien.
 */
export function readInvoices(raw: unknown): Invoice[] {
  const list = Array.isArray(raw) ? raw : [];
  return list.flatMap((f: RawInvoice) =>
    typeof f?.id !== "number"
      ? []
      : [
          {
            id: f.id,
            // Sans numéro, on nomme la facture par son identifiant plutôt que par un
            // blanc : c'est ce qu'on cite au comptable.
            numero: (f.label ?? "").trim() || `n° ${f.id}`,
            amountExclTax: count(f.price),
            /* ⚠️ SANS TVA, LE PAYÉ EST LE HORS-TAXES (bug #211) : les factures
               enregistrées avant la correction portent encore le TTC dans `priceTva`, et
               la liste annonçait « 1 052,80 € TTC » pour 1 019 € débités. */
            amountInclTax:
              f.withTva === false || f.withTva === 0
                ? count(f.price)
                : count(f.priceTva),
            date:
              typeof f.bill?.date === "string" ? f.bill.date : "",
            /* La clé qui départage. Absente des deux côtés — une facture d'un format
               qu'on ne connaît pas — on prend le générateur du format MAJORITAIRE : il
               a 38 797 factures pour lui contre 205. */
            generator:
              f.bill?.resume?.totalHTString === undefined &&
              f.bill?.resume?.totalHT !== undefined
                ? "gayakoa"
                : "standard",
          },
        ],
  );
}

export async function fetchInvoices(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<Invoice[]> {
  const answer = await apiFetch<{ invoices?: unknown } | null>(
    `/v2/domains/${domainId}/events/${eventId}/invoicesV2/me`,
    { signal },
  );
  return readInvoices(answer?.invoices);
}

/**
 * LA LANGUE DU DOCUMENT.
 *
 * ⚠️ DEUX SEULEMENT, ET C'EST LE GÉNÉRATEUR QUI LE DIT — pas une simplification de
 * notre part. Demander « IT » à un générateur qui n'a que `t.FR` et `t.EN` produit un
 * document dont chaque intitulé est `undefined`. Un italien reçoit donc une facture
 * ANGLAISE, ce qui est le moins mauvais des deux.
 */
export function invoiceLanguage(locale: string): "FR" | "EN" {
  return locale.toLowerCase().startsWith("fr") ? "FR" : "EN";
}

/**
 * UN NOM DE FICHIER QUI DIT CE QUE C'EST.
 *
 * ⚠️ LE NUMÉRO PORTE UNE BARRE OBLIQUE (« 2025/35 »), qui n'a pas sa place dans un nom
 * de fichier : sous Windows il est refusé, ailleurs il se lirait comme un dossier. On
 * la remplace, et on garde le numéro lisible — c'est lui qu'on cherche dans un dossier
 * de téléchargements six mois plus tard.
 *
 * Le mot « Facture » vient de l'écran, dans la langue du lecteur (bug #489 : écrit ici
 * en dur, le site anglais téléchargeait « Facture-2026-13.pdf »). Il passe par le même
 * filtre : c'est un texte du dictionnaire, que le back office peut réécrire.
 */
export function fileName(invoice: Invoice, word: string): string {
  const clean = (s: string) => s.replace(/[/\\:*?"<>|]/g, "-").trim();
  return `${clean(word)}-${clean(invoice.numero)}.pdf`;
}

/**
 * DEMANDE LE PDF.
 *
 * ⚠️ AU CLIC, ET NON AU CHARGEMENT DE L'ÉCRAN. La V1 le demande dans un `useEffect`
 * pour CHAQUE facture dès l'affichage (`Invoices.jsx`), donc elle fabrique tous les
 * documents même quand personne n'en ouvre aucun — un PDF par facture, à chaque
 * confirmation affichée. Ici, rien ne part tant qu'on ne le demande pas.
 */
export async function downloadInvoice(
  domainId: number,
  eventId: number,
  userId: number,
  invoice: Invoice,
  locale: string,
): Promise<Blob> {
  const params = new URLSearchParams({ language: invoiceLanguage(locale) });
  // Le générateur qui sait lire CETTE facture — déterminé à la lecture, pas ici.
  if (invoice.generator === "gayakoa") params.set("gayakoa", "true");

  return apiBlob(
    `/domains/${domainId}/events/${eventId}/users/${userId}/invoices/${invoice.id}?${params}`,
    { api: "apix", method: "POST" },
  );
}
